import { CatalogIndex } from './fd-catalog-engine.js';

const FACETS = ['categories', 'sizes', 'colors'];
const PARAMS = { categories: 'fd_category', sizes: 'fd_size', colors: 'fd_color' };
const SORTS = new Set(['default', 'price-asc', 'price-desc', 'title-asc']);
const CUSTOM_PARAMS = ['fd_query', 'fd_category', 'fd_size', 'fd_color', 'fd_min', 'fd_max', 'fd_sort'];

function jsonFrom(root, selector) {
  const source = root.querySelector(selector);
  if (!source) throw new Error(selector);
  return JSON.parse(source.textContent);
}

function nonnegative(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, Math.round(number)) : null;
}

function nativeFilterKey(key) {
  return key.startsWith('filter.') || key === 'page';
}

function facetValue(value) {
  return String(value).normalize('NFKD').replace(/\p{M}/gu, '').toUpperCase().toLowerCase().replace(/\s+/gu, ' ').trim();
}

class FavoriteDayCatalog extends HTMLElement {
  constructor() {
    super();
    this.generation = 0;
    this.cards = new Map();
    this.records = new Map();
    this.loadedPages = new Set();
    this.failedPages = new Set();
    this.pendingPages = [];
    this.activeLoads = 0;
    this.stateRevision = 0;
    this.pageCache = new Map();
    this.cacheTick = 0;
    this.activeCardLoads = new Map();
    this.pendingCardPages = [];
    this.failedCardPages = new Set();
    this.neededCardPages = new Set();
    this.visibleIDs = [];
    this.indexReady = false;
    this.indexLoading = true;
    this.indexError = false;
    this.initialized = false;
    this.complete = false;
    this.loading = false;
    this.error = false;
  }

  connectedCallback() {
    this.controller = new AbortController();
    this.generation += 1;
    const generation = this.generation;
    try {
      if (!this.initialized) this.initialize();
      else if (this.wasDisconnected) this.grid.replaceChildren();
      this.wasDisconnected = false;
      this.state = this.readState();
      this.stateRevision += 1;
      this.visibleLimit = this.pageSize;
      this.bindControls();
      this.syncControls();
      this.refresh();
      this.sidebar.hidden = false;
      this.querySelectorAll('[data-fd-native-filters]').forEach((element) => { element.hidden = true; });
      this.classList.add('fd-catalog--ready');
      this.startIndexing(generation);
    } catch {
      this.classList.remove('fd-catalog--ready');
      if (this.sidebar) this.sidebar.hidden = true;
      this.querySelectorAll('[data-fd-native-filters]').forEach((element) => { element.hidden = false; });
    }
  }

  disconnectedCallback() {
    this.wasDisconnected = true;
    this.generation += 1;
    this.controller?.abort();
    clearTimeout(this.searchTimer);
    this.activeLoads = 0;
    this.activeCardLoads.clear();
    this.pendingCardPages = [];
    this.indexLoading = false;
    this.loading = false;
  }

  initialize() {
    this.config = jsonFrom(this, '[data-fd-catalog-config]');
    this.form = this.querySelector('[data-fd-catalog-form]');
    this.sidebar = this.querySelector('[data-fd-catalog-sidebar]');
    const details = this.querySelector('[data-fd-catalog-details]');
    if (details && matchMedia('(max-width: 749px)').matches) details.open = false;
    this.grid = this.querySelector('[data-testid="product-grid"]');
    if (!this.form || !this.sidebar || !this.grid || !this.config.sectionId) throw new Error('catalog');
    this.labels = this.config.labels || {};
    this.search = this.querySelector('[data-fd-catalog-search]');
    this.sort = this.querySelector('[data-fd-catalog-sort]');
    this.minimum = this.querySelector('[data-fd-price-min]');
    this.maximum = this.querySelector('[data-fd-price-max]');
    this.minimumInput = this.querySelector('[data-fd-price-input-min]');
    this.maximumInput = this.querySelector('[data-fd-price-input-max]');
    this.priceOutput = this.querySelector('[data-fd-price-output]');
    this.priceRange = this.querySelector('[data-fd-price-range]');
    this.status = this.querySelector('[data-fd-catalog-status]');
    this.empty = this.querySelector('[data-fd-catalog-empty]');
    this.retryButton = this.querySelector('[data-fd-catalog-retry]');
    this.moreButton = this.querySelector('[data-fd-catalog-more]');
    this.pageSize = Math.max(1, Math.floor(Number(this.config.pageSize) || 24));
    this.debounceMs = Math.min(500, Math.max(100, Number(this.config.debounceMs) || 220));
    this.totalPages = 1;
    this.indexPageSize = 250;
    this.totalItems = Math.max(0, Number(this.config.totalItems) || 0);
    this.currentPage = Math.max(1, Math.floor(Number(this.config.currentPage) || 1));
    this.numberFormatter = new Intl.NumberFormat(this.config.locale || document.documentElement.lang || undefined);
    this.currencyFormatter = new Intl.NumberFormat(this.config.locale || document.documentElement.lang || undefined, {
      style: 'currency', currency: this.config.currency || 'USD'
    });
    this.baseURL = new URL(this.config.collectionURL, window.location.origin);
    if (this.baseURL.origin !== window.location.origin) throw new Error('origin');
    [...this.baseURL.searchParams.keys()].forEach((key) => {
      if (nativeFilterKey(key) || CUSTOM_PARAMS.includes(key) || key === 'section_id') this.baseURL.searchParams.delete(key);
    });
    const currentURL = new URL(window.location.href);
    const serverSort = currentURL.searchParams.get('sort_by') || this.config.sortBy;
    if (serverSort) this.baseURL.searchParams.set('sort_by', serverSort);
    if (serverSort === 'best-selling' && this.sort && this.labels.bestSelling) this.sort.options[0].textContent = this.labels.bestSelling;
    if (currentURL.pathname !== this.baseURL.pathname && currentURL.pathname.endsWith(this.baseURL.pathname)) {
      this.baseURL.pathname = currentURL.pathname;
    }
    const previewTheme = currentURL.searchParams.get('preview_theme_id');
    if (previewTheme) this.baseURL.searchParams.set('preview_theme_id', previewTheme);
    this.needsCleanFirstPage = this.currentPage !== 1 || [...currentURL.searchParams.keys()].some(nativeFilterKey);
    this.engine = this.newIndex();
    this.addRecords(this, this.currentPage, this.pageSize);
    const initialIDs = new Set();
    this.grid.querySelectorAll('li[data-product-id]').forEach((card) => {
      const id = String(card.dataset.productId);
      this.cards.set(id, card.cloneNode(true));
      initialIDs.add(id);
    });
    if (!this.needsCleanFirstPage) this.pageCache.set(this.currentPage, { ids: initialIDs, lastUsed: ++this.cacheTick });
    if (this.retryButton && this.labels.retry) this.retryButton.textContent = this.labels.retry;
    if (this.moreButton && this.labels.loadMore) this.moreButton.textContent = this.labels.loadMore;
    if (this.empty && this.labels.empty) this.empty.textContent = this.labels.empty;
    this.initialized = true;
  }

  newIndex() {
    return new CatalogIndex({
      sizeOptionNames: this.config.sizeOptionNames || [],
      colorOptionNames: this.config.colorOptionNames || []
    });
  }

  readState() {
    const params = new URL(window.location.href).searchParams;
    const state = {
      query: params.get('fd_query') || '',
      minPrice: nonnegative(params.get('fd_min')),
      maxPrice: nonnegative(params.get('fd_max')),
      sort: SORTS.has(params.get('fd_sort')) ? params.get('fd_sort') : 'default'
    };
    FACETS.forEach((facet) => { state[facet] = [...new Set(params.getAll(PARAMS[facet]).map(facetValue).filter(Boolean))]; });
    if (state.minPrice !== null && state.maxPrice !== null && state.minPrice > state.maxPrice) {
      [state.minPrice, state.maxPrice] = [state.maxPrice, state.minPrice];
    }
    return state;
  }

  writeState(mode = 'push') {
    const url = new URL(window.location.href);
    [...url.searchParams.keys()].forEach((key) => {
      if (nativeFilterKey(key) || CUSTOM_PARAMS.includes(key)) url.searchParams.delete(key);
    });
    if (this.state.query.trim()) url.searchParams.set('fd_query', this.state.query.trim());
    FACETS.forEach((facet) => this.state[facet].forEach((value) => url.searchParams.append(PARAMS[facet], value)));
    if (this.state.minPrice !== null) url.searchParams.set('fd_min', String(this.state.minPrice));
    if (this.state.maxPrice !== null) url.searchParams.set('fd_max', String(this.state.maxPrice));
    if (this.state.sort !== 'default') url.searchParams.set('fd_sort', this.state.sort);
    if (url.href !== window.location.href) {
      window.history[mode === 'replace' ? 'replaceState' : 'pushState'](window.history.state, '', url);
    }
  }

  bindControls() {
    const options = { signal: this.controller.signal };
    this.form.addEventListener('input', (event) => {
      if (event.target === this.search) {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => {
          this.state.query = this.search.value;
          this.applyState();
        }, this.debounceMs);
      } else if (event.target === this.minimum || event.target === this.maximum) {
        this.changePrice(event.target, false);
      }
    }, options);
    this.form.addEventListener('change', (event) => {
      const facet = event.target.dataset.fdFacet;
      if (FACETS.includes(facet)) {
        this.state[facet] = [...this.form.querySelectorAll(`input[data-fd-facet="${facet}"]:checked`)].map((input) => input.value);
        this.applyState();
      } else if (event.target === this.sort) {
        this.state.sort = SORTS.has(this.sort.value) ? this.sort.value : 'default';
        this.applyState();
      } else if (event.target === this.minimumInput || event.target === this.maximumInput) {
        this.changePrice(event.target, true);
      }
    }, options);
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      clearTimeout(this.searchTimer);
      if (this.search) this.state.query = this.search.value;
      if (document.activeElement === this.minimumInput || document.activeElement === this.maximumInput) {
        this.changePrice(document.activeElement, true);
      } else {
        this.applyState();
      }
    }, options);
    this.form.addEventListener('reset', (event) => {
      event.preventDefault();
      clearTimeout(this.searchTimer);
      this.state = { query: '', categories: [], sizes: [], colors: [], minPrice: null, maxPrice: null, sort: 'default' };
      this.applyState('push', false);
    }, options);
    this.moreButton?.addEventListener('click', () => {
      this.visibleLimit += this.pageSize;
      this.stateRevision += 1;
      this.render();
    }, options);
    this.retryButton?.addEventListener('click', () => this.retry(), options);
    window.addEventListener('popstate', () => {
      clearTimeout(this.searchTimer);
      this.state = this.readState();
      this.stateRevision += 1;
      this.visibleLimit = this.pageSize;
      this.syncControls();
      this.refresh();
    }, options);
  }

  applyState(historyMode = 'push', readSearch = true) {
    clearTimeout(this.searchTimer);
    if (readSearch && this.search) this.state.query = this.search.value;
    this.stateRevision += 1;
    this.visibleLimit = this.pageSize;
    this.syncControls();
    this.refresh();
    this.writeState(historyMode);
  }

  changePrice(input, decimal) {
    if (!input) return;
    const isMinimum = input === this.minimum || input === this.minimumInput;
    const value = input.value === '' ? null : nonnegative(Number(input.value) * (decimal ? 100 : 1));
    const bounds = this.priceBounds();
    if (isMinimum) {
      const maximum = this.state.maxPrice ?? bounds.max;
      const next = value === null ? null : Math.min(maximum, Math.max(bounds.min, value));
      this.state.minPrice = next === bounds.min ? null : next;
    } else {
      const minimum = this.state.minPrice ?? bounds.min;
      const next = value === null ? null : Math.max(minimum, Math.min(bounds.max, value));
      this.state.maxPrice = next === bounds.max ? null : next;
    }
    this.applyState(decimal ? 'push' : 'replace');
    if (decimal) {
      const updatedBounds = this.priceBounds();
      const selected = isMinimum ? this.state.minPrice ?? updatedBounds.min : this.state.maxPrice ?? updatedBounds.max;
      input.value = (selected / 100).toFixed(2);
    }
  }

  priceBounds() {
    const price = this.facets?.price || {};
    const knownMinimum = nonnegative(price.min) ?? 0;
    const knownMaximum = nonnegative(price.max) ?? knownMinimum;
    const values = [knownMinimum, knownMaximum, this.state.minPrice, this.state.maxPrice].filter((value) => value !== null);
    return { min: Math.min(...values), max: Math.max(...values) };
  }

  syncControls() {
    if (this.search && this.search.value !== this.state.query) this.search.value = this.state.query;
    if (this.sort) this.sort.value = this.state.sort;
    this.form.querySelectorAll('input[data-fd-facet]').forEach((input) => {
      input.checked = this.state[input.dataset.fdFacet]?.includes(input.value) || false;
    });
  }

  refresh() {
    this.facets = this.engine.getFacets(this.state);
    this.updateFacets();
    this.updateSelections();
    this.updatePrice();
    this.render();
  }

  updateFacets() {
    FACETS.forEach((facet) => {
      const container = this.querySelector(`[data-fd-catalog-facet="${facet}"]`);
      if (!container) return;
      const known = new Map([...container.querySelectorAll('input[data-fd-facet]')].map((input) => [input.value, input]));
      const values = [...(this.facets[facet] || [])];
      this.state[facet].forEach((value) => {
        if (!values.some((item) => item.value === value)) values.push({ value, label: value, count: 0 });
      });
      const currentValues = new Set(values.map((item) => item.value));
      known.forEach((input, value) => {
        if (!currentValues.has(value)) input.closest('label')?.remove();
      });
      values.forEach((item) => {
        let input = known.get(item.value);
        if (!input) {
          const label = document.createElement('label');
          label.className = 'fd-catalog__facet-choice';
          input = document.createElement('input');
          input.type = 'checkbox';
          input.value = item.value;
          input.name = PARAMS[facet];
          input.dataset.fdFacet = facet;
          const text = document.createElement('span');
          text.dataset.fdFacetLabel = '';
          const count = document.createElement('span');
          count.className = 'fd-catalog__facet-count';
          count.dataset.fdFacetCount = '';
          count.setAttribute('aria-hidden', 'true');
          label.append(input, text, count);
          container.append(label);
        }
        input.checked = this.state[facet].includes(item.value);
        input.disabled = this.complete && !input.checked && !item.count;
        const label = input.closest('label');
        label.querySelector('[data-fd-facet-label]').textContent = item.label;
        label.querySelector('[data-fd-facet-count]').textContent = this.numberFormatter.format(item.count || 0);
      });
      const focused = document.activeElement;
      const hadFocus = container.contains(focused);
      const labels = new Map([...container.querySelectorAll('input[data-fd-facet]')].map((input) => [input.value, input.closest('label')]));
      values.forEach((item, index) => {
        const label = labels.get(item.value);
        const reference = container.children[index] || null;
        if (label === reference) return;
        if (typeof container.moveBefore === 'function') container.moveBefore(label, reference);
        else container.insertBefore(label, reference);
      });
      if (hadFocus && document.activeElement !== focused && focused.isConnected) focused.focus({ preventScroll: true });
      const fieldset = this.querySelector(`[data-fd-facet-fieldset="${facet}"]`);
      if (fieldset) fieldset.hidden = values.length === 0;
    });
  }

  updateSelections() {
    const container = this.querySelector('[data-fd-active-filters]');
    if (!container) return;
    container.replaceChildren();
    let count = 0;
    FACETS.forEach((facet) => this.state[facet].forEach((value) => {
      const label = this.facets[facet]?.find((item) => item.value === value)?.label || value;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'fd-catalog__active-filter';
      button.textContent = `${label} ×`;
      button.setAttribute('aria-label', (this.labels.removeFilter || '[value]').replaceAll('[value]', label));
      button.addEventListener('click', () => {
        const hadFocus = document.activeElement === button;
        this.state[facet] = this.state[facet].filter((item) => item !== value);
        this.applyState();
        if (hadFocus) (container.querySelector('button') || this.querySelector('[data-fd-catalog-details] summary'))?.focus();
      });
      container.append(button);
      count += 1;
    }));
    container.hidden = count === 0;
    const badge = this.querySelector('[data-fd-active-count]');
    if (badge) { badge.textContent = count ? `(${this.numberFormatter.format(count)})` : ''; badge.hidden = !count; }
  }

  updatePrice() {
    const bounds = this.priceBounds();
    const minimum = this.state.minPrice ?? bounds.min;
    const maximum = this.state.maxPrice ?? bounds.max;
    [this.minimum, this.maximum].forEach((input) => {
      if (!input) return;
      input.min = String(bounds.min);
      input.max = String(bounds.max);
      input.step = '1';
      input.disabled = bounds.min === bounds.max;
    });
    if (this.minimum) {
      this.minimum.value = String(minimum);
      this.minimum.setAttribute('aria-valuetext', this.currencyFormatter.format(minimum / 100));
      this.minimum.style.zIndex = minimum === maximum && maximum === bounds.max ? '3' : '';
    }
    if (this.maximum) {
      this.maximum.value = String(maximum);
      this.maximum.setAttribute('aria-valuetext', this.currencyFormatter.format(maximum / 100));
    }
    [this.minimumInput, this.maximumInput].forEach((input) => {
      if (!input) return;
      input.min = String(bounds.min / 100);
      input.max = String(bounds.max / 100);
      input.step = '0.01';
    });
    if (this.minimumInput && document.activeElement !== this.minimumInput) this.minimumInput.value = (minimum / 100).toFixed(2);
    if (this.maximumInput && document.activeElement !== this.maximumInput) this.maximumInput.value = (maximum / 100).toFixed(2);
    if (this.priceOutput) {
      this.priceOutput.textContent = `${this.currencyFormatter.format(minimum / 100)} – ${this.currencyFormatter.format(maximum / 100)}`;
    }
    const span = bounds.max - bounds.min;
    const start = span ? ((minimum - bounds.min) / span) * 100 : 0;
    const end = span ? ((maximum - bounds.min) / span) * 100 : 100;
    this.priceRange?.style.setProperty('--fd-price-start', `${start}%`);
    this.priceRange?.style.setProperty('--fd-price-end', `${end}%`);
  }

  render(revision = this.stateRevision) {
    if (revision !== this.stateRevision || !this.isConnected) return;
    const matching = this.engine.query(this.state).map(String);
    this.visibleIDs = matching.slice(0, this.visibleLimit);
    this.prepareCardRequests();
    const visible = this.visibleIDs.filter((id) => this.cards.has(id));
    const desiredIDs = new Set(visible);
    [...this.grid.children].forEach((node) => {
      if (!desiredIDs.has(String(node.dataset.productId))) node.remove();
    });
    visible.forEach((id, index) => {
      const variant = this.engine.matchingVariant(id, this.state);
      const variantId = variant?.id || '';
      if (String(this.grid.children[index]?.dataset.productId) === id && (this.grid.children[index]?.dataset.fdVariantId || '') === variantId) return;
      const previous = [...this.grid.children].find((node) => String(node.dataset.productId) === id);
      previous?.remove();
      const card = this.cards.get(id).cloneNode(true);
      card.dataset.fdVariantId = variantId;
      if (variantId) {
        const productCard = card.querySelector('product-card');
        if (productCard) {
          productCard.dataset.fdVariantId = variantId;
          if (variant.mediaId) productCard.dataset.fdVariantMediaId = variant.mediaId;
          const productPath = productCard.querySelector('a[ref="productCardLink"]')?.getAttribute('href');
          if (productPath) {
            const path = new URL(productPath, location.origin).pathname;
            productCard.querySelectorAll('a[href]').forEach((link) => {
              const url = new URL(link.getAttribute('href'), location.origin);
              if (url.origin === location.origin && url.pathname === path) { url.searchParams.set('variant', variantId); link.href = url.href; }
            });
          }
        }
      }
      this.grid.insertBefore(card, this.grid.children[index] || null);
    });
    while (this.grid.children.length > visible.length) this.grid.lastElementChild.remove();
    this.evictCards();
    const waitingForCards = [...this.neededCardPages].some((page) => this.activeCardLoads.has(page)) || this.pendingCardPages.length > 0;
    this.loading = this.indexLoading || waitingForCards;
    this.error = this.indexError || [...this.neededCardPages].some((page) => this.failedCardPages.has(page));
    if (this.empty) this.empty.hidden = matching.length > 0 || !this.complete;
    if (this.moreButton) {
      this.moreButton.hidden = matching.length <= this.visibleLimit;
      this.moreButton.disabled = matching.length <= this.visibleLimit;
    }
    if (this.retryButton) this.retryButton.hidden = !this.error;
    const messages = [];
    const countLabel = matching.length === 1 ? this.labels.resultSingular || this.labels.result : this.labels.result;
    if (countLabel) messages.push(countLabel.replaceAll('[count]', this.numberFormatter.format(matching.length)));
    if (this.loading && this.labels.loading) messages.push(this.labels.loading);
    if (this.error && this.labels.error) messages.push(this.labels.error);
    if (this.status) this.status.textContent = messages.join(' ');
    this.setAttribute('aria-busy', this.loading ? 'true' : 'false');
  }

  normalizeRecords(root, page, pageSize) {
    const records = jsonFrom(root, '[data-fd-catalog-records]');
    if (!Array.isArray(records)) throw new Error('records');
    return records.filter((record) => record && record.id !== null && record.id !== undefined).map((record, index) => {
      const prices = Array.isArray(record.variantPrices) ? record.variantPrices : [];
      const options = Array.isArray(record.variantOptions) ? record.variantOptions : [];
      const availability = Array.isArray(record.variantAvailable) ? record.variantAvailable : [];
      const ids = Array.isArray(record.variantIds) ? record.variantIds : [];
      const mediaIds = Array.isArray(record.variantMediaIds) && record.variantMediaIds.length === prices.length ? record.variantMediaIds : [];
      if (!Array.isArray(record.variants) && (prices.length !== options.length || Number(record.variantsCount) !== prices.length)) throw new Error('variants');
      const variants = Array.isArray(record.variants) ? record.variants : prices.map((price, variantIndex) => ({
        id: ids[variantIndex], mediaId: mediaIds[variantIndex],
        price: Number(price),
        options: Array.isArray(options[variantIndex]) ? options[variantIndex] : [],
        available: availability[variantIndex] !== false
      }));
      const position = record.position === null || record.position === undefined ? NaN : Number(record.position);
      return {
        id: String(record.id), title: record.title, description: record.description,
        categories: record.categories, optionNames: record.optionNames, variants,
        position: Number.isFinite(position) ? position : (page - 1) * pageSize + index
      };
    });
  }

  addRecords(root, page, pageSize) {
    return this.addNormalizedRecords(this.normalizeRecords(root, page, pageSize));
  }

  addNormalizedRecords(records) {
    const additions = [];
    records.forEach((record) => {
      const id = String(record.id);
      if (this.records.has(id)) return;
      this.records.set(id, { id, position: record.position });
      additions.push(record);
    });
    this.engine.add(additions);
    return additions;
  }

  async fetchSection(sectionId, page, generation) {
    const url = new URL(this.baseURL);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('page', String(page));
    const response = await fetch(url, {
      credentials: 'same-origin', cache: 'no-store', signal: this.controller.signal,
      headers: { Accept: 'text/html' }
    });
    if (!response.ok) throw new Error(String(response.status));
    const source = await response.text();
    if (generation !== this.generation || !this.isConnected) throw new DOMException('', 'AbortError');
    return new DOMParser().parseFromString(source, 'text/html');
  }

  async fetchIndexPage(page, generation) {
    const parsed = await this.fetchSection(this.config.indexSection || 'fd-catalog-index', page, generation);
    const root = parsed.querySelector('[data-fd-catalog-index]');
    if (!root) throw new Error('index');
    return root;
  }

  async fetchCardPage(page, generation) {
    const parsed = await this.fetchSection(this.config.sectionId, page, generation);
    const root = [...parsed.querySelectorAll('fd-catalog')].find((element) => element.dataset.sectionId === String(this.config.sectionId));
    if (!root) throw new Error('section');
    return root;
  }

  async startIndexing(generation) {
    this.indexError = false;
    this.indexLoading = !this.indexReady || this.loadedPages.size < this.totalPages;
    this.complete = this.indexReady && !this.indexLoading;
    this.render();
    if (!this.indexReady) {
      try {
        const root = await this.fetchIndexPage(1, generation);
        if (generation !== this.generation || !this.isConnected) return;
        const config = jsonFrom(root, '[data-fd-index-config]');
        const pageSize = Math.max(1, Math.floor(Number(config.pageSize) || 250));
        const records = this.normalizeRecords(root, 1, pageSize);
        this.engine = this.newIndex();
        this.records.clear();
        this.loadedPages.clear();
        this.failedPages.clear();
        this.totalPages = Math.max(1, Math.floor(Number(config.totalPages) || 1));
        this.totalItems = Math.max(0, Number(config.totalItems) || 0);
        this.indexPageSize = pageSize;
        this.addNormalizedRecords(records);
        this.loadedPages.add(1);
        this.indexReady = true;
        this.reconcileInitialCardPages();
        this.needsCleanFirstPage = false;
        this.writeState('replace');
        this.refresh();
      } catch (error) {
        if (generation !== this.generation || error.name === 'AbortError') return;
        this.indexLoading = false;
        this.complete = false;
        this.indexError = true;
        this.render();
        return;
      }
    }
    if (generation !== this.generation || !this.isConnected) return;
    this.pendingPages = [];
    for (let page = 1; page <= this.totalPages; page += 1) {
      if (!this.loadedPages.has(page)) this.pendingPages.push(page);
    }
    this.pumpIndex(generation);
  }

  pumpIndex(generation) {
    if (generation !== this.generation || !this.isConnected) return;
    while (this.activeLoads < 2 && this.pendingPages.length) {
      const page = this.pendingPages.shift();
      this.activeLoads += 1;
      this.fetchIndexPage(page, generation).then((root) => {
        if (generation !== this.generation || !this.isConnected) return;
        this.addRecords(root, page, this.indexPageSize);
        this.loadedPages.add(page);
        this.failedPages.delete(page);
        this.refresh();
      }).catch((error) => {
        if (generation !== this.generation || error.name === 'AbortError') return;
        this.failedPages.add(page);
        this.indexError = true;
      }).finally(() => {
        if (generation !== this.generation || !this.isConnected) return;
        this.activeLoads -= 1;
        this.pumpIndex(generation);
      });
    }
    this.indexLoading = this.activeLoads > 0 || this.pendingPages.length > 0;
    this.complete = !this.indexLoading && this.failedPages.size === 0 && this.loadedPages.size >= this.totalPages;
    this.indexError = this.failedPages.size > 0;
    this.render();
  }

  cardPageFor(id) {
    const position = this.records.get(id)?.position;
    return Number.isFinite(position) ? Math.floor(position / this.pageSize) + 1 : null;
  }

  reconcileInitialCardPages() {
    const pages = new Map();
    this.cards.forEach((_, id) => {
      const page = this.cardPageFor(id);
      if (page === null) return;
      if (!pages.has(page)) pages.set(page, { ids: new Set(), lastUsed: ++this.cacheTick });
      pages.get(page).ids.add(id);
    });
    this.pageCache = pages;
  }

  prepareCardRequests() {
    if (!this.indexReady) return;
    const missingPages = new Set();
    this.visibleIDs.forEach((id) => {
      const page = this.cardPageFor(id);
      if (page === null) return;
      const cached = this.pageCache.get(page);
      if (cached) cached.lastUsed = ++this.cacheTick;
      if (!this.cards.has(id)) missingPages.add(page);
    });
    this.neededCardPages = missingPages;
    this.pendingCardPages = [...missingPages].filter((page) => !this.activeCardLoads.has(page) && !this.failedCardPages.has(page));
    this.pumpCards();
  }

  pumpCards() {
    while (this.activeCardLoads.size < 2 && this.pendingCardPages.length) {
      const page = this.pendingCardPages.shift();
      const generation = this.generation;
      const revision = this.stateRevision;
      this.activeCardLoads.set(page, revision);
      this.fetchCardPage(page, generation).then((root) => {
        if (generation !== this.generation || !this.isConnected) return;
        if (revision !== this.stateRevision && !this.neededCardPages.has(page)) return;
        const ids = new Set();
        root.querySelectorAll('[data-testid="product-grid"] li[data-product-id]').forEach((card) => {
          const id = String(card.dataset.productId);
          if (!this.records.has(id)) return;
          this.cards.set(id, card.cloneNode(true));
          ids.add(id);
        });
        this.pageCache.set(page, { ids, lastUsed: ++this.cacheTick });
        const missing = this.visibleIDs.some((id) => this.cardPageFor(id) === page && !this.cards.has(id));
        if (missing) this.failedCardPages.add(page);
        else this.failedCardPages.delete(page);
      }).catch((error) => {
        if (generation !== this.generation || error.name === 'AbortError') return;
        if (revision === this.stateRevision || this.neededCardPages.has(page)) this.failedCardPages.add(page);
      }).finally(() => {
        if (generation !== this.generation || !this.isConnected) return;
        this.activeCardLoads.delete(page);
        this.render(this.stateRevision);
      });
    }
  }

  evictCards() {
    if (!this.indexReady) return;
    const visible = new Set(this.visibleIDs);
    while (this.pageCache.size > 4) {
      const pages = [...this.pageCache].sort((left, right) => left[1].lastUsed - right[1].lastUsed);
      const entry = pages.find(([, cached]) => ![...cached.ids].some((id) => visible.has(id))) || pages[0];
      this.pageCache.delete(entry[0]);
      entry[1].ids.forEach((id) => {
        if (!visible.has(id)) this.cards.delete(id);
      });
    }
    const cachedIDs = new Set();
    this.pageCache.forEach((page) => page.ids.forEach((id) => cachedIDs.add(id)));
    this.cards.forEach((_, id) => {
      if (!visible.has(id) && !cachedIDs.has(id)) this.cards.delete(id);
    });
  }

  retry() {
    if (this.indexLoading || [...this.neededCardPages].some((page) => this.activeCardLoads.has(page))) return;
    this.failedPages.clear();
    this.failedCardPages.clear();
    this.indexError = false;
    this.startIndexing(this.generation);
  }
}

if (!customElements.get('fd-catalog')) customElements.define('fd-catalog', FavoriteDayCatalog);
